import { useQuery } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { ListResponse } from '@shared/schemas/pagination';
import type {
  ReportKpis,
  ReportPreset,
  ReportRunView,
  ReportTable,
  ReportType,
} from '@shared/schemas/report';

/* The queries behind /admin/reports. ROADMAP.md §10.4 and §10.5. */

export interface ReportParams {
  type: ReportType;
  preset: ReportPreset;
  from?: string;
  to?: string;
  employeeId?: string;
  clientId?: string;
}

export function useReport(params: ReportParams) {
  return useQuery({
    queryKey: ['admin', 'reports', 'table', params],
    queryFn: () => api.get<ReportTable>(`/api/admin/reports${queryString({ ...params })}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * The four cards — **defect P1**.
 *
 * **Keyed on the window and not on the report type**, deliberately: switching
 * from Summary to Jobs asks a different question of the same period, and the
 * four counts above are about the period. Including `type` in the key would
 * refetch four unchanged numbers on every tab click, which is the shape
 * `request-budget.spec.ts` exists to catch.
 */
export function useReportKpis(params: ReportParams) {
  const window = {
    // `type` is required by the endpoint's schema and does not affect the
    // answer, so it is pinned rather than passed through.
    type: 'summary' as const,
    preset: params.preset,
    from: params.from,
    to: params.to,
  };

  return useQuery({
    queryKey: ['admin', 'reports', 'kpis', window],
    queryFn: () => api.get<ReportKpis>(`/api/admin/reports/kpis${queryString({ ...window })}`),
    placeholderData: (previous) => previous,
  });
}

export function useReportRuns(page: number) {
  return useQuery({
    queryKey: ['admin', 'reports', 'runs', page],
    queryFn: () =>
      api.get<ListResponse<ReportRunView>>(`/api/admin/reports/runs${queryString({ page })}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * The download URL.
 *
 * **A plain link rather than a fetch**, which is the whole reason this is a
 * function returning a string. `fetch` would put the file in memory and need a
 * blob URL and a synthetic click to get it onto disk; an `<a href download>`
 * hands the job to the browser, which already knows how to show progress, resume
 * and put it in the right folder. The session is a cookie, so it authenticates
 * itself.
 */
export function reportDownloadUrl(params: ReportParams, format: 'csv' | 'pdf'): string {
  return `/api/admin/reports/download${queryString({ ...params, format })}`;
}
